// src/pages/MyDrops.js
// The user's saved generations (images + videos) → /api/drops.
//
// Everything here is the user's own work, so there is no "Recreate": you are
// already the author. Per-card actions are Post to Drift and Delete, revealed
// on hover rather than pinned under every tile, so the wall stays image-first.
//
// Selection mode turns the header into a selection bar and deletes in one call
// via /drops/bulk-delete — which has existed in the API all along without a
// caller. "Delete all" lives in there too, rather than beside the page title.
//
// Pagination is real: /drops returns has_more and caps limit at 50, and this
// page used to ask for 40 and ignore it, so anything past 40 was invisible.

import { useCallback, useEffect, useMemo, useState } from "react";
import MediaViewer from "../components/MediaViewer";
import { drops, drift } from "../api";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "image", label: "Images" },
  { id: "video", label: "Videos" },
];

const PAGE = 50; // the backend's maximum

/* ---------------------------------------------------------------- helpers */

// A video drop's thumbnail_url falls back to the video URL on the backend when
// no still exists, so an <img> pointed at it renders broken. Detect that and
// use a <video> poster frame instead.
const isPlayable = (d) => d.type === "video" && d.thumbnail_url && d.thumbnail_url === d.url;

function when(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Today / This week / Earlier, in that order, skipping empty groups.
function group(items) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const weekAgo = startOfToday - 6 * 86400000;
  const buckets = { Today: [], "This week": [], Earlier: [] };
  items.forEach((d) => {
    const t = new Date(d.created_at || 0).getTime();
    if (t >= startOfToday) buckets.Today.push(d);
    else if (t >= weekAgo) buckets["This week"].push(d);
    else buckets.Earlier.push(d);
  });
  return Object.entries(buckets).filter(([, list]) => list.length);
}

const Tick = ({ on }) => (
  <span className={`drop-tick ${on ? "drop-tick--on" : ""}`}>
    {on && (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6 9 17l-5-5" />
      </svg>
    )}
  </span>
);

/* ------------------------------------------------------------------- page */

export default function MyDrops() {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState("");
  const [viewer, setViewer] = useState(null);
  const [toast, setToast] = useState("");
  const [confirm, setConfirm] = useState(null); // { title, body, label, run }
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [menu, setMenu] = useState(null); // drop_id whose ⋯ menu is open

  const say = (m) => {
    setToast(m);
    window.setTimeout(() => setToast(""), 2600);
  };

  const load = useCallback(async (nextPage = 1) => {
    setLoading(true);
    setErr("");
    try {
      const d = await drops.list({
        type: filter === "all" ? undefined : filter,
        page: nextPage,
        limit: PAGE,
      });
      const batch = d.drops || [];
      setItems((prev) => (nextPage === 1 ? batch : [...prev, ...batch]));
      setMore(!!d.pagination?.has_more);
      setPage(nextPage);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    setPicked(new Set());
    setSelecting(false);
    load(1);
  }, [load]);

  const counts = useMemo(() => ({
    all: items.length,
    image: items.filter((d) => d.type === "image").length,
    video: items.filter((d) => d.type === "video").length,
  }), [items]);

  const groups = useMemo(() => group(items), [items]);

  /* ----------------------------------------------------------- mutations */

  const removeOne = (drop) => setConfirm({
    title: "Delete this drop?",
    body: "It will be removed from your library. This cannot be undone.",
    label: "Delete",
    run: async () => {
      await drops.remove(drop.drop_id);
      setItems((xs) => xs.filter((x) => x.drop_id !== drop.drop_id));
      say("Deleted");
    },
  });

  const removePicked = () => setConfirm({
    title: `Delete ${picked.size} ${picked.size === 1 ? "drop" : "drops"}?`,
    body: "They will be removed from your library. This cannot be undone.",
    label: `Delete ${picked.size}`,
    run: async () => {
      await drops.bulkDelete([...picked]);
      setItems((xs) => xs.filter((x) => !picked.has(x.drop_id)));
      say(`Deleted ${picked.size}`);
      setPicked(new Set());
      setSelecting(false);
    },
  });

  const removeAll = () => setConfirm({
    title: "Delete everything?",
    body: `All ${items.length} of your drops will be removed. This cannot be undone.`,
    label: "Delete all",
    danger: true,
    run: async () => {
      await drops.removeAll();
      setItems([]);
      say("All drops deleted");
      setSelecting(false);
    },
  });

  const share = async (drop) => {
    try {
      await drift.share({ drop_id: drop.drop_id, type: drop.type });
      say("Posted to Drift");
    } catch (e) {
      say(e.message || "Could not post to Drift");
    }
  };

  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /* -------------------------------------------------------------- render */

  const card = (d) => {
    const on = picked.has(d.drop_id);
    return (
      <div
        className={`mcard drop-card ${on ? "drop-card--on" : ""}`}
        key={d.drop_id}
        onClick={() => (selecting ? toggle(d.drop_id) : setViewer(items.indexOf(d)))}
      >
        <div className="mcard__media">
          {isPlayable(d) ? (
            <video src={d.url} muted playsInline preload="metadata" />
          ) : (
            <img src={d.thumbnail_url || d.url} alt={d.prompt || ""} loading="lazy" />
          )}

          <span className="mcard__badge">
            {d.type === "video" ? (d.duration ? `▶ 0:${String(d.duration).padStart(2, "0")}` : "▶ video") : "IMAGE"}
          </span>

          {selecting ? <Tick on={on} /> : null}

          {!selecting && (
            <div className="mcard__overlay">
              <span className="drop-card__when">{when(d.created_at)}</span>
              <span className="drop-card__acts">
                <button
                  className="mcard__remix"
                  onClick={(e) => { e.stopPropagation(); setMenu(menu === d.drop_id ? null : d.drop_id); }}
                >
                  ⋯
                </button>
              </span>
            </div>
          )}

          {menu === d.drop_id && (
            <div className="drop-menu" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => { setMenu(null); share(d); }}>Post to Drift</button>
              <button className="drop-menu__danger" onClick={() => { setMenu(null); removeOne(d); }}>Delete</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="page" onClick={() => menu && setMenu(null)}>
      {selecting ? (
        <div className="drop-selbar">
          <button className="drop-selbar__close" onClick={() => { setSelecting(false); setPicked(new Set()); }} aria-label="Cancel">✕</button>
          <b>{picked.size} selected</b>
          <button
            className="drop-selbar__link"
            onClick={() => setPicked(picked.size === items.length ? new Set() : new Set(items.map((d) => d.drop_id)))}
          >
            {picked.size === items.length ? "Clear all" : "Select all"}
          </button>
          <span className="drop-selbar__spacer" />
          <button className="btn btn--ghost" onClick={removeAll}>Delete all</button>
          <button className="btn btn--danger" disabled={!picked.size} onClick={removePicked}>
            Delete {picked.size || ""}
          </button>
        </div>
      ) : (
        <div className="page__head drop-head">
          <div>
            <h1>My drops</h1>
            <p className="page__sub">
              {items.length
                ? `${counts.all} loaded · ${counts.image} images · ${counts.video} videos`
                : "Everything you've generated, in one place."}
            </p>
          </div>
          {items.length > 0 && (
            <button className="btn btn--ghost" onClick={() => setSelecting(true)}>Select</button>
          )}
        </div>
      )}

      <div className="chips">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            className={`chip ${filter === f.id ? "chip--active" : ""}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}{filter === f.id && items.length ? ` ${counts[f.id]}` : ""}
          </button>
        ))}
      </div>

      {err && <div className="form-error center">{err}</div>}

      {loading && page === 1 ? (
        <div className="masonry">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="mcard mcard--skeleton" style={{ height: 140 + (i % 3) * 50 }} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="empty">Nothing here yet — head to Image or Video to create something.</div>
      ) : (
        <>
          {groups.map(([label, list]) => (
            <section className="drop-group" key={label}>
              <div className="drop-group__head">
                <h2>{label}</h2>
                <span>{list.length} {list.length === 1 ? "item" : "items"}</span>
              </div>
              <div className="masonry">{list.map(card)}</div>
            </section>
          ))}

          {more && (
            <div className="drop-more">
              <button className="btn btn--ghost" disabled={loading} onClick={() => load(page + 1)}>
                {loading ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}

      {viewer !== null && (
        <MediaViewer
          items={items}
          index={viewer}
          onNav={setViewer}
          onClose={() => setViewer(null)}
        />
      )}

      {confirm && (
        <div className="modal-backdrop" onClick={() => setConfirm(null)}>
          <div className="modal drop-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="modal__body">
              <h3>{confirm.title}</h3>
              <p>{confirm.body}</p>
              <div className="drop-confirm__row">
                <button className="btn btn--ghost" onClick={() => setConfirm(null)}>Cancel</button>
                <button
                  className="btn btn--danger"
                  onClick={async () => {
                    const run = confirm.run;
                    setConfirm(null);
                    try { await run(); } catch (e) { say(e.message || "Something went wrong"); }
                  }}
                >
                  {confirm.label}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
